import { Routes } from '@angular/router'
import { _deepCopy, _mapToObject, _numberEnumValues, NumberEnum } from '@naturalcycles/js-lib'
import {
  BreakpointAction,
  ChoiceFunction,
  CycleChangesBreakpointOption,
  GoalBreakpointOption,
  QuizDataInput,
  shortQuizGoalOptions,
} from '@naturalcycles/shared'
import { mapOptionToPage } from '@src/app/constants/breakpoints/cycle-changes.js'
import {
  allQuizSections,
  sectionCycleChangesEnd,
  sectionCycleChangesStart,
  sectionSexQuestions,
} from '@src/app/constants/quiz.js'
import {
  addPageAtEndOfSection,
  addPageAtStartOfSection,
  addPageInSectionAtIndex,
  filterPagesFromSection,
} from '@src/app/core/util/quiz.util.js'
import { Page } from '@src/app/shared/typings/enum/pages.js'
import { DefaultSections } from '@src/app/shared/typings/enum/quiz.js'
import { SignupQuizFlow } from '@src/app/shared/typings/interfaces/quiz.js'

export const ab312Routes: Routes = [
  {
    path: Page.introChapter,
    loadComponent: () =>
      import('./intro-chapter/intro-chapter.page.component').then(m => m.IntroChapterPage),
  },
  {
    path: Page.goalChapter,
    loadComponent: () =>
      import('./goal-chapter/goal-chapter.page.component').then(m => m.GoalChapterPage),
  },
  {
    path: Page.cycleChapter,
    loadComponent: () =>
      import('./cycle-chapter/cycle-chapter.page.component').then(m => m.CycleChapterPage),
  },
  {
    path: Page.changesChapter,
    loadComponent: () =>
      import('./changes-chapter/changes-chapter.page.component').then(m => m.ChangesChapterPage),
  },
  {
    path: Page.routineChapter,
    loadComponent: () =>
      import('./routine-chapter/routine-chapter.page.component').then(m => m.RoutineChapterPage),
  },
]

export const ab312QuizPages = ab312Routes.map(r => r.path)

function getAb312GoalPreventLikeTransformFunction(
  goalBreakpointOption: GoalBreakpointOption,
  existingBreakpointAction: BreakpointAction<GoalBreakpointOption, Page, QuizDataInput> | undefined,
): ChoiceFunction<Page, QuizDataInput> {
  return (flow, data) => {
    const existingChoiceFunction = existingBreakpointAction?.[goalBreakpointOption]
    flow = existingChoiceFunction?.(flow, data) || flow

    // Intro chapter should come after the privacy page
    let lastPageBeforeIntroChapterIndex = flow[allQuizSections.intro]?.indexOf(Page.privacy)
    // If privacy page is not found due to some other AB test or whatever, let's just add it
    // at the end of the intro section
    lastPageBeforeIntroChapterIndex ||= flow[allQuizSections.intro]?.length || 0
    flow = addPageInSectionAtIndex(
      flow,
      Page.introChapter,
      DefaultSections.intro,
      lastPageBeforeIntroChapterIndex + 1,
    )
    flow = addPageAtStartOfSection(flow, Page.goalChapter, DefaultSections.goal)

    // Undo changes from other goal options, in case the user changed their mind
    flow = filterPagesFromSection(
      flow,
      [Page.fertilityGoalInfo, Page.plannedPregnancyTiming],
      DefaultSections.intro,
    )

    return flow
  }
}

function getAb312GoalPlanTransformFunction(
  existingBreakpointAction: BreakpointAction<GoalBreakpointOption, Page, QuizDataInput> | undefined,
): ChoiceFunction<Page, QuizDataInput> {
  return (flow, data) => {
    const existingChoiceFunction = existingBreakpointAction?.[GoalBreakpointOption.plan]
    flow = existingChoiceFunction?.(flow, data) || flow

    let privacyIndex = flow[allQuizSections.intro]?.indexOf(Page.privacy)
    privacyIndex ||= flow[allQuizSections.intro]?.length || 0
    flow = addPageInSectionAtIndex(flow, Page.introChapter, DefaultSections.intro, privacyIndex + 1)

    flow = addPageAtEndOfSection(flow, Page.fertilityGoalInfo, DefaultSections.intro)
    flow = addPageAtEndOfSection(flow, Page.plannedPregnancyTiming, DefaultSections.intro)

    flow[allQuizSections.goal] = [Page.goalChapter, ...sectionSexQuestions.slice(1)]

    return flow
  }
}

export function getAb312ShortGoalBreakpointAction(
  existingBreakpointAction: BreakpointAction<GoalBreakpointOption, Page, QuizDataInput> | undefined,
  goal: GoalBreakpointOption,
): ChoiceFunction<Page, QuizDataInput> {
  return (flow, data) => {
    flow = existingBreakpointAction?.[goal]?.(flow, data) || flow

    // Undo changes from other goal options, in case the user changed their mind
    flow = filterPagesFromSection(
      flow,
      [Page.fertilityGoalInfo, Page.plannedPregnancyTiming, Page.introChapter],
      DefaultSections.intro,
    )

    return flow
  }
}

export function getAb312GoalBreakpointAction(
  existingBreakpointAction: BreakpointAction<GoalBreakpointOption, Page, QuizDataInput> | undefined,
): BreakpointAction<GoalBreakpointOption, Page, QuizDataInput> {
  return {
    ...existingBreakpointAction,
    ..._mapToObject(shortQuizGoalOptions, goal => [
      goal,
      getAb312ShortGoalBreakpointAction(existingBreakpointAction, goal),
    ]),
    [GoalBreakpointOption.prevent]: getAb312GoalPreventLikeTransformFunction(
      GoalBreakpointOption.prevent,
      existingBreakpointAction,
    ),
    [GoalBreakpointOption.preventNowPlanLater]: getAb312GoalPreventLikeTransformFunction(
      GoalBreakpointOption.preventNowPlanLater,
      existingBreakpointAction,
    ),
    [GoalBreakpointOption.improveWellbeing]: getAb312GoalPreventLikeTransformFunction(
      GoalBreakpointOption.improveWellbeing,
      existingBreakpointAction,
    ),
    [GoalBreakpointOption.manageSymptoms]: getAb312GoalPreventLikeTransformFunction(
      GoalBreakpointOption.manageSymptoms,
      existingBreakpointAction,
    ),
    [GoalBreakpointOption.trackCycle]: getAb312GoalPreventLikeTransformFunction(
      GoalBreakpointOption.trackCycle,
      existingBreakpointAction,
    ),
    [GoalBreakpointOption.undecided]: getAb312GoalPreventLikeTransformFunction(
      GoalBreakpointOption.undecided,
      existingBreakpointAction,
    ),
    [GoalBreakpointOption.plan]: getAb312GoalPlanTransformFunction(existingBreakpointAction),
  }
}

export function getAb312CycleChangesBreakpointAction(): BreakpointAction<
  CycleChangesBreakpointOption,
  Page,
  QuizDataInput
> {
  const newSectionCycleChangesStart = [Page.changesChapter, ...sectionCycleChangesStart]

  // Copied from original breakpoint
  const commonTransformFunction = (
    flow: SignupQuizFlow,
    choice: CycleChangesBreakpointOption,
  ): SignupQuizFlow => {
    // only undefined if the user selects skip. They should not land here in that case
    const page = mapOptionToPage[choice]!

    const flowCopy = _deepCopy(flow)

    if (flowCopy[allQuizSections.cycleChanges]?.includes(page)) {
      return filterPagesFromSection(flowCopy, [page], DefaultSections.cycleChanges)
    }

    /**
     * Add page in the section
     */
    const startIndex = newSectionCycleChangesStart.length
    if (!flowCopy[allQuizSections.cycleChanges]?.length) {
      // only change of logic from original breakpoint is using newSectionCycleChangesStart instead of sectionCycleChangesStart
      flowCopy[allQuizSections.cycleChanges] = [
        ...newSectionCycleChangesStart,
        ...sectionCycleChangesEnd,
      ]
    }
    flowCopy[allQuizSections.cycleChanges]!.splice(startIndex, 0, page)
    return flowCopy
  }

  const cycleChanges: BreakpointAction<CycleChangesBreakpointOption, Page, QuizDataInput> = {
    [CycleChangesBreakpointOption.emotions]: flow => {
      return commonTransformFunction(flow, CycleChangesBreakpointOption.emotions)
    },
    [CycleChangesBreakpointOption.bleeding]: flow => {
      return commonTransformFunction(flow, CycleChangesBreakpointOption.bleeding)
    },
    [CycleChangesBreakpointOption.skinChanges]: flow => {
      return commonTransformFunction(flow, CycleChangesBreakpointOption.skinChanges)
    },
    [CycleChangesBreakpointOption.sexAndLibido]: flow => {
      return commonTransformFunction(flow, CycleChangesBreakpointOption.sexAndLibido)
    },
    [CycleChangesBreakpointOption.cervicalMucus]: flow => {
      return commonTransformFunction(flow, CycleChangesBreakpointOption.cervicalMucus)
    },
    [CycleChangesBreakpointOption.pain]: flow => {
      return commonTransformFunction(flow, CycleChangesBreakpointOption.pain)
    },
    [CycleChangesBreakpointOption.skip]: flow => {
      flow[allQuizSections.cycleChanges] = [
        ...newSectionCycleChangesStart,
        ...sectionCycleChangesEnd,
      ]
      return flow
    },
  }

  return cycleChanges
}

// BreakpointOption is for example `GoalBreakpointOption`
export function getAb312AllChaptersBreakpointWrapper<BreakpointOption extends number>(
  existingBreakpointAction: BreakpointAction<BreakpointOption, Page, QuizDataInput> | undefined,
  breakpointOption: NumberEnum,
  isClearblue: boolean,
): BreakpointAction<GoalBreakpointOption, Page, QuizDataInput> {
  return _mapToObject(_numberEnumValues(breakpointOption), option => [
    option,
    (flow, data) => {
      flow = existingBreakpointAction?.[option as BreakpointOption]?.(flow, data) || flow
      const isShortQuizGoal =
        !!data?.fertilityGoal && shortQuizGoalOptions.includes(data.fertilityGoal)

      if (isShortQuizGoal) {
        return flow
      }

      // A list of [chapter page, the section where that chapter should be added]
      const chaptersAndSections: [Page, DefaultSections][] = [
        [Page.goalChapter, DefaultSections.goal],
        [Page.cycleChapter, DefaultSections.cycle],
        [Page.changesChapter, DefaultSections.cycleChanges],
      ]
      if (!isClearblue) {
        chaptersAndSections.push([Page.routineChapter, DefaultSections.routine])
      }

      chaptersAndSections.forEach(([page, section]) => {
        // If they're in short goal, we "un-apply" the chapter page as they might've selected a different goal previously
        // and we don't want short goal users to see the test
        if (isShortQuizGoal) {
          return filterPagesFromSection(flow, [page], section)
        }
        // Otherwise, we add the chapter page to the start of the section
        return addPageAtStartOfSection(flow, page, section)
      })

      flow = filterPagesFromSection(flow, sectionSexQuestions, DefaultSections.cycle)

      return flow
    },
  ]) as BreakpointAction<GoalBreakpointOption, Page, QuizDataInput>
}

export const imageLinkMap: Record<DefaultSections, Record<'mobile' | 'desktop', string>> = {
  [DefaultSections.intro]: {
    mobile: 'https://www.datocms-assets.com/21281/1738667325-chapter-37.png',
    desktop: 'https://www.datocms-assets.com/21281/1738242403-section-1-desktop-1.jpg',
  },
  [DefaultSections.goal]: {
    mobile: 'https://www.datocms-assets.com/21281/1737974820-goal-chapter-bg.jpg',
    desktop: 'https://www.datocms-assets.com/21281/1738242452-section-2-desktop.jpg',
  },
  [DefaultSections.cycle]: {
    mobile: 'https://www.datocms-assets.com/21281/1737974819-cycle-chapter-bg.jpg',
    desktop: 'https://www.datocms-assets.com/21281/1738242516-section-3-desktop.jpg',
  },
  [DefaultSections.cycleChanges]: {
    mobile: 'https://www.datocms-assets.com/21281/1737974819-changes-chapter-bg.jpg',
    desktop: 'https://www.datocms-assets.com/21281/1738242535-section-3-desktop-1.jpg',
  },
  [DefaultSections.routine]: {
    mobile: 'https://www.datocms-assets.com/21281/1737974819-routine-chapter-bg.jpg',
    desktop: 'https://www.datocms-assets.com/21281/1738242568-section-3-desktop-2.jpg',
  },
}
